import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Logo from '@/components/Logo';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_LEN = 6;

/**
 * Shared UI/logic for both the invite "set password" flow and the
 * forgot-password "reset password" flow. The only differences between the two
 * are the copy and the backend endpoint they POST to, so we keep this generic
 * and let the wrapper pages configure it via props.
 */
export default function PasswordSetupForm({
  title,
  subtitle,
  endpoint,
  successMessage,
  redirectQuery,
  submitLabel,
  testIdPrefix,
}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Missing or malformed link. Please use the exact link from your email.');
      return;
    }
    if (password.length < MIN_LEN) {
      setError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}${endpoint}`, {
        token,
        new_password: password,
      });
      setDone(true);
      // Auto-redirect after a short beat so the user sees the confirmation.
      setTimeout(() => navigate(`/login?${redirectQuery}`), 1200);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen shine-gradient-bg flex items-center justify-center px-4 py-10">
      <Card className="shine-card w-full max-w-md p-6 sm:p-8 rounded-2xl shadow-2xl">
        <div className="mb-6"><Logo size="md" /></div>
        <h2 className="text-2xl font-bold text-white">{title}</h2>
        <p className="text-sm text-slate-400 mt-1">{subtitle}</p>

        {done ? (
          <div className="mt-6 space-y-4" data-testid={`${testIdPrefix}-success`}>
            <div className="flex items-start gap-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-4">
              <CheckCircle2 className="h-5 w-5 text-emerald-300 shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-100">{successMessage}</div>
            </div>
            <p className="text-xs text-slate-400 text-center">Redirecting to sign in…</p>
          </div>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={submit}>
            {!token && (
              <div className="flex items-start gap-2 text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>This link doesn&apos;t include a token. Please use the exact URL from your email.</span>
              </div>
            )}

            <div>
              <Label htmlFor="password" className="text-slate-200">New password</Label>
              <Input
                id="password"
                data-testid={`${testIdPrefix}-password`}
                type="password"
                required
                minLength={MIN_LEN}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 bg-[#0f2240] border-amber-500/20 text-white focus-visible:ring-amber-400"
                placeholder={`At least ${MIN_LEN} characters`}
              />
            </div>
            <div>
              <Label htmlFor="confirm" className="text-slate-200">Confirm password</Label>
              <Input
                id="confirm"
                data-testid={`${testIdPrefix}-confirm`}
                type="password"
                required
                minLength={MIN_LEN}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 bg-[#0f2240] border-amber-500/20 text-white focus-visible:ring-amber-400"
                placeholder="Re-enter the password"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || !token}
              className="w-full font-semibold text-white"
              style={{ background: 'linear-gradient(180deg,#4ec178,#2da655)' }}
              data-testid={`${testIdPrefix}-submit`}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
            </Button>

            <Link
              to="/login"
              className="block text-xs text-amber-300 hover:text-amber-200 text-center pt-1"
              data-testid={`${testIdPrefix}-back-link`}
            >
              Back to sign in
            </Link>
          </form>
        )}
      </Card>
    </div>
  );
}
